import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CATEGORY_IDS, type CategoryId } from "@orvis/core";

/**
 * The TV / Radio list filters, kept in the URL (`?cat=news&region=…&near=1`)
 * so the back button and a reload both land on the same view.
 */
export interface BrowseFilterState {
  category?: CategoryId;
  region?: string;
  near: boolean;
}

export function useBrowseFilter(): [BrowseFilterState, (patch: Partial<BrowseFilterState>) => void] {
  const [params, setParams] = useSearchParams();

  const state = useMemo<BrowseFilterState>(() => {
    const cat = params.get("cat");
    return {
      category: (CATEGORY_IDS as readonly string[]).includes(cat ?? "") ? (cat as CategoryId) : undefined,
      region: params.get("region") || undefined,
      near: params.get("near") === "1",
    };
  }, [params]);

  const update = useCallback(
    (patch: Partial<BrowseFilterState>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const set = (k: string, v: string | undefined) => (v ? next.set(k, v) : next.delete(k));
          if ("category" in patch) set("cat", patch.category);
          if ("region" in patch) set("region", patch.region);
          if ("near" in patch) set("near", patch.near ? "1" : undefined);
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  return [state, update];
}
